import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../App';

const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : '—');

const Card = ({ title, icon, children, testid, tone = 'border-border' }) => (
  <div className={`p-5 rounded-2xl border bg-surface ${tone}`} data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-3">
      <i className={`fa-solid ${icon} mr-2 text-primary`} />{title}
    </p>
    {children}
  </div>
);

function DeedDocument({ report }) {
  const [busy, setBusy] = useState(false);
  const dd = report.deed_document || {};
  const latest = dd.latest_deed || (report.chain_of_title || [])[0];
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/title/deed-abstract', { address: report.address, city: report.city, state: report.state, zip: report.zip }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `deed-${(report.address || 'property').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Could not build the deed document'); }
    setBusy(false);
  };
  return (
    <Card title="Deed document" icon="fa-file-signature" testid="title-deed-document" tone={dd.official_image_url ? 'border-emerald-300' : 'border-border'}>
      {latest ? (
        <div className="text-sm space-y-1 mb-3" data-testid="deed-latest">
          <p className="font-bold text-text-primary">{latest.deed_type || 'Deed'} · {String(latest.date || '').slice(0, 10)}{latest.price ? ` · ${money(latest.price)}` : ''}</p>
          {latest.document_number && <p className="text-[11px] text-text-muted">Document # <b>{latest.document_number}</b>{latest.recorded ? ` · recorded ${String(latest.recorded).slice(0, 10)}` : ''}</p>}
          {(latest.buyer || latest.seller) && <p className="text-[11px] text-text-muted">{latest.seller || '—'} → {latest.buyer || '—'}</p>}
        </div>
      ) : <p className="text-sm text-text-muted mb-3">No recorded deed returned by the title sources.</p>}
      <div className="flex flex-wrap gap-2">
        {dd.official_image_url && <a href={dd.official_image_url} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid="deed-official-image-btn"><i className="fa-solid fa-file-pdf mr-1.5" />Open recorded deed</a>}
        <button onClick={download} disabled={busy} className={dd.official_image_url ? 'btn-secondary text-xs' : 'btn-primary text-xs'} data-testid="deed-download-btn">
          {busy ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-solid fa-download mr-1.5" />}Download deed abstract (PDF)
        </button>
        <a href={dd.state_portal || dd.recorder_search_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs" data-testid="deed-recorder-link"><i className="fa-solid fa-landmark mr-1.5" />{dd.county ? `${dd.county} recorder` : 'County recorder'}</a>
      </div>
      <p className="text-[11px] text-text-muted mt-2" data-testid="deed-note">{dd.note || 'Order a certified copy from the county recorder for legal use.'}</p>
    </Card>
  );
}

export default function TitleCheck() {
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [providers, setProviders] = useState(null);
  const [config, setConfig] = useState(null);
  const [showKeys, setShowKeys] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get('/api/leads', { params: { limit: 100 } })
      .then(r => setLeads(r.data.leads || []))
      .catch(() => {});
    api.get('/api/title/providers').then(r => setProviders(r.data)).catch(() => {});
    api.get('/api/title/config').then(r => setConfig(r.data)).catch(() => {});
    api.get('/api/title/reports', { params: { limit: 10 } }).then(r => setHistory(r.data.reports || [])).catch(() => {});
  }, []);

  const pickLead = (id) => {
    setLeadId(id);
    const l = leads.find(x => x.id === id);
    if (l) { setAddress(l.address || ''); setCity(l.city || ''); setState(l.state || ''); setZip(l.zip || l.zip_code || ''); }
  };

  const run = async () => {
    if (!address.trim()) { toast.error('Enter an address or pick a lead'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/title/search', { lead_id: leadId || undefined, address, city, state, zip });
      setReport(res.data);
      toast.success(res.data.simulated ? 'Sample title report built (SIMULATED)' : 'Title pulled');
      api.get('/api/title/reports', { params: { limit: 10 } }).then(r => setHistory(r.data.reports || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Title pull failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="title-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Lead Management</p>
          <h1 className="text-3xl font-black tracking-tight text-text-primary">Title & Lien Check</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Who legally owns the roof, who has to sign, what's already borrowed against it and anything that
            would stop a funder cold — pulled before a rep ever writes a contract.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="title-providers">
          {(providers?.providers || []).map(p => (
            <span key={p.key}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-bold border ${p.configured ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-surface-alt border-border text-text-muted'}`}>
              <i className={`fa-solid ${p.configured ? 'fa-circle-check' : 'fa-circle-dashed'} mr-1.5`} />{p.label}
            </span>
          ))}
        </div>
      </div>

      {providers && !providers.any_configured && (
        <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="title-simulated-banner">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-triangle-exclamation mt-0.5" />
            <span className="flex-1">
              <b>SIMULATED output.</b> No title data source is connected yet, so the engine returns a realistic sample
              so you can see the exact report. It flips to a real pull the moment a Deed &amp; Sales, Parcel, Lien Monitor
              or Full Title Package key is pasted in <Link to="/settings" className="underline font-bold">Settings</Link>.
            </span>
            <button onClick={() => setShowKeys(v => !v)} className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-[11px] font-black whitespace-nowrap" data-testid="title-getkey-toggle">
              {showKeys ? 'Hide' : 'Where do I get a key?'}
            </button>
          </div>
        </div>
      )}

      {showKeys && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="title-getkey-panel">
          {(providers?.providers || []).map(p => (
            <div key={p.key} className={`p-5 rounded-2xl border bg-surface ${p.recommended ? 'border-emerald-300' : 'border-border'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-black text-text-primary">{p.label}</p>
                  <p className="text-[11px] text-text-muted">{p.vendor}</p>
                </div>
                {p.recommended && <span className="px-2 py-0.5 rounded-lg bg-emerald-500 text-white text-[10px] font-black">START HERE</span>}
              </div>
              <p className="text-sm text-text-primary mt-3">{p.best_for}</p>
              <p className="text-[12px] text-text-muted mt-2"><i className="fa-solid fa-tag mr-1.5" />{p.cost}</p>
              <div className="mt-3 flex items-center gap-2">
                <a href={p.signup} target="_blank" rel="noreferrer"
                  className="px-4 py-2 rounded-xl bg-text-primary text-surface text-[12px] font-bold hover:opacity-90">
                  <i className="fa-solid fa-arrow-up-right-from-square mr-1.5" />Get the key
                </a>
                <code className="px-2 py-1 rounded-lg bg-surface-alt border border-border text-[11px] text-text-muted">{p.env}</code>
              </div>
              <p className="text-[11px] text-text-muted mt-2">Paste it in Settings ▸ Key Manager (or hand it to me) and this page goes live.</p>
            </div>
          ))}
        </div>
      )}

      {config && (
        <div className="flex flex-wrap items-center gap-5 p-4 rounded-2xl border border-border bg-surface" data-testid="title-auto-config">
          <button
            onClick={async () => {
              try {
                const res = await api.put('/api/title/config', { auto_on_proposal: !config.auto_on_proposal });
                setConfig(res.data);
                toast.success(res.data.auto_on_proposal ? 'Auto title pull is ON' : 'Auto title pull is OFF');
              } catch (err) { toast.error(err.response?.data?.detail || 'Managers only'); }
            }}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold ${config.auto_on_proposal ? 'bg-emerald-500 text-white' : 'bg-surface-alt border border-border text-text-muted'}`}
            data-testid="title-auto-toggle">
            <i className={`fa-solid ${config.auto_on_proposal ? 'fa-circle-check' : 'fa-circle-pause'} mr-2`} />
            Auto-pull on proposal stage
          </button>
          <p className="text-[12px] text-text-muted max-w-xl">
            Every lead that reaches <b>proposal</b> (or has a proposal built) gets its title pulled automatically, the rep
            gets pinged the second a blocker shows up, and it re-checks after {config.recheck_after_days} days.
          </p>
        </div>
      )}

      <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="title-search-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">From a lead</label>
            <select value={leadId} onChange={e => pickLead(e.target.value)} className="input-field w-64" data-testid="title-lead-select">
              <option value="">— manual address —</option>
              {leads.map(l => <option key={l.id} value={l.id}>{(l.owner_name || l.name || l.address)} · {l.city}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Street address</label>
            <input value={address} onChange={e => setAddress(e.target.value)} className="input-field w-full" placeholder="123 Main St" data-testid="title-address" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">City</label>
            <input value={city} onChange={e => setCity(e.target.value)} className="input-field w-40" data-testid="title-city" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">State</label>
            <input value={state} onChange={e => setState(e.target.value)} className="input-field w-20" data-testid="title-state" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">ZIP</label>
            <input value={zip} onChange={e => setZip(e.target.value)} className="input-field w-28" data-testid="title-zip" />
          </div>
          <button onClick={run} disabled={loading}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-sm font-bold shadow hover:shadow-lg disabled:opacity-60"
            data-testid="title-run-btn">
            {loading ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Pulling title…</> : <><i className="fa-solid fa-file-contract mr-2" />Pull full title</>}
          </button>
        </div>
      </div>

      {report && (
        <div className="space-y-4" data-testid="title-report">
          <div className={`p-6 rounded-2xl border ${report.clear_to_close ? 'border-emerald-300 bg-emerald-50/50' : 'border-rose-300 bg-rose-50/50'}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Owner of record</p>
                <h2 className="text-2xl font-black text-text-primary" data-testid="title-owner">{report.owner_of_record || '—'}</h2>
                <p className="text-sm text-text-muted mt-1">{report.address}{report.city ? `, ${report.city}` : ''} {report.state} {report.zip}</p>
                <p className="text-sm font-bold text-text-primary mt-3" data-testid="title-signature">
                  <i className="fa-solid fa-signature mr-2 text-primary" />{report.signature_requirement || report.vesting}
                </p>
              </div>
              <div className="text-right">
                <span className={`px-4 py-2 rounded-xl text-sm font-black ${report.clear_to_close ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`} data-testid="title-clear-badge">
                  {report.clear_to_close ? 'CLEAR TO CLOSE' : `${report.blockers?.length || 0} BLOCKER${report.blockers?.length === 1 ? '' : 'S'}`}
                </span>
                <p className="text-[11px] text-text-muted mt-2">{report.completeness}% of the record filled</p>
                {report.simulated && <p className="text-[11px] font-black text-amber-600 mt-1">SIMULATED SAMPLE</p>}
              </div>
            </div>
            {(report.blockers || []).length > 0 && (
              <ul className="mt-4 space-y-1" data-testid="title-blockers">
                {report.blockers.map((b, i) => (
                  <li key={i} className="text-sm text-rose-700 flex gap-2"><i className="fa-solid fa-circle-exclamation mt-1" />{b}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Market value', value: money(report.market_value) },
              { label: 'Total encumbrances', value: money(report.encumbrance_total) },
              { label: 'Estimated equity', value: money(report.estimated_equity) },
              { label: 'Annual tax', value: money(report.tax_amount) },
            ].map(s => (
              <div key={s.label} className="p-4 rounded-2xl border border-border bg-surface">
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{s.label}</p>
                <p className="text-xl font-black text-text-primary">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card title="Parcel & legal description" icon="fa-map-location-dot" testid="title-parcel">
              <dl className="space-y-2 text-sm">
                {[['APN / parcel', report.apn], ['County', report.county], ['Acreage', report.acreage],
                  ['Year built', report.year_built], ['Property type', report.property_type],
                  ['Owner mailing', report.mailing_address], ['Absentee owner', report.absentee ? 'Yes' : 'No']].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-1">
                    <dt className="text-text-muted">{k}</dt>
                    <dd className="font-bold text-text-primary text-right">{v || v === 0 ? String(v) : '—'}</dd>
                  </div>
                ))}
              </dl>
              {report.legal_description && (
                <p className="mt-3 text-[12px] text-text-primary bg-surface-alt border border-border rounded-xl p-3 font-mono">{report.legal_description}</p>
              )}
            </Card>

            <Card title="Chain of title" icon="fa-link" testid="title-chain">
              {(report.chain_of_title || []).length === 0 ? <p className="text-sm text-text-muted">No transfers on record.</p> : (
                <ol className="space-y-3">
                  {report.chain_of_title.map((t, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-2 h-2 mt-2 rounded-full bg-primary shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-text-primary">{String(t.date || '').slice(0, 10)} · {money(t.price)}</p>
                        <p className="text-[11px] text-text-muted">{t.deed_type || 'Deed'}{t.seller ? ` · ${t.seller} → ${t.buyer}` : ''}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <DeedDocument report={report} />

            <Card title="Mortgages & loans" icon="fa-building-columns" testid="title-mortgages">
              {(report.mortgages || []).length === 0 ? <p className="text-sm text-text-muted">No open loans on record.</p> : (
                <div className="space-y-2">
                  {report.mortgages.map((m, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-surface-alt border border-border">
                      <div>
                        <p className="text-sm font-black text-text-primary">{m.position || 'Loan'} · {m.lender || 'Lender not stated'}</p>
                        <p className="text-[11px] text-text-muted">{String(m.date || '').slice(0, 10)} · {m.type || 'mortgage'}</p>
                      </div>
                      <p className="text-sm font-black text-text-primary">{money(m.amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Liens, judgments & distress flags" icon="fa-gavel" testid="title-liens">
              {(report.liens || []).length === 0 && (report.judgments || []).length === 0 ? (
                <p className="text-sm text-text-muted">Nothing filed against the property.</p>
              ) : (
                <div className="space-y-2">
                  {[...(report.liens || []), ...(report.judgments || [])].map((l, i) => (
                    <div key={i} className="p-3 rounded-xl bg-surface-alt border border-border">
                      <p className="text-sm font-black text-text-primary">{(l && l.type) || 'Filing'} · {money(l && l.amount)}</p>
                      <p className="text-[11px] text-text-muted">{(l && l.claimant) || ''} {l && l.filed ? `· filed ${String(l.filed).slice(0, 10)}` : ''} {l && l.status ? `· ${l.status}` : ''}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(report.flags || {}).map(([k, v]) => (
                  <span key={k} className={`px-3 py-1.5 rounded-xl text-[11px] font-bold border ${v ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-surface-alt border-border text-text-muted'}`}>
                    <i className={`fa-solid ${v ? 'fa-triangle-exclamation' : 'fa-check'} mr-1.5`} />{k.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          <p className="text-[11px] text-text-muted" data-testid="title-note">
            {report.note} Pulled {String(report.pulled_at || '').slice(0, 16).replace('T', ' ')} UTC
            {report.sources?.length ? ` · sources: ${report.sources.length}` : ''}
            {(report.errors || []).length ? ` · ${report.errors.length} source(s) unavailable` : ''}
          </p>
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden" data-testid="title-history">
          <p className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted bg-surface-alt">Recent pulls</p>
          <table className="w-full text-sm">
            <tbody>
              {history.map(h => (
                <tr key={h.id} className="border-t border-border">
                  <td className="px-5 py-2.5 font-bold text-text-primary">{h.address}</td>
                  <td className="px-3 py-2.5 text-text-muted">{h.city} {h.state}</td>
                  <td className="px-3 py-2.5">{h.report?.owner_of_record || '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded-lg text-[11px] font-bold ${h.report?.clear_to_close ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {h.report?.clear_to_close ? 'clear' : `${h.report?.blockers?.length || 0} blockers`}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-[11px] text-text-muted">{String(h.pulled_at || '').slice(0, 16).replace('T', ' ')}{h.simulated ? ' · sample' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
